import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "@/lib/motion";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const GoogleBtn = ({ onClick }) => (
    <button onClick={onClick} data-testid="google-register-btn" className="flex w-full items-center justify-center gap-3 rounded-full border border-caramel/20 bg-white py-3 font-medium text-roast transition-colors hover:bg-cream-100">
        <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.47 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z"/></svg>
        Continue with Google
    </button>
);

export default function Register() {
    const { register, loginWithGoogle, user } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (user) navigate("/account"); }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        const res = await register(name, email, password);
        setLoading(false);
        if (res.ok) { toast.success("Account created — welcome to Sweet Pearls! 🍌"); navigate("/account"); }
        else setErr(res.error);
    };

    return (
        <div className="grid min-h-screen lg:grid-cols-2">
            <div className="flex items-center justify-center px-6 py-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
                    <Link to="/" className="font-serif text-3xl font-semibold text-roast">Sweet <span className="italic text-caramel">Pearls</span></Link>
                    <h1 className="mt-8 font-serif text-3xl text-roast">Create your account</h1>
                    <p className="mt-1 text-mocha">Save favorites, reorder fast & earn rewards.</p>

                    <div className="mt-7"><GoogleBtn onClick={loginWithGoogle} /></div>
                    <div className="my-5 flex items-center gap-3 text-xs text-mocha"><span className="h-px flex-1 bg-caramel/15" /> or <span className="h-px flex-1 bg-caramel/15" /></div>

                    <form onSubmit={submit} className="space-y-3">
                        <Input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="h-12 rounded-xl border-caramel/15 bg-white focus-visible:ring-caramel" />
                        <Input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="h-12 rounded-xl border-caramel/15 bg-white focus-visible:ring-caramel" />
                        <div className="relative">
                            <Input data-testid="register-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 chars)" className="h-12 rounded-xl border-caramel/15 bg-white pr-12 focus-visible:ring-caramel" />
                            <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="register-password-toggle" aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-mocha hover:text-caramel">
                                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {err && <p data-testid="register-error" className="text-sm text-destructive">{err}</p>}
                        <Button type="submit" disabled={loading} data-testid="register-submit" className="h-12 w-full rounded-full bg-caramel text-cream-50 hover:bg-caramel-dark">{loading ? "Creating…" : "Create account"}</Button>
                    </form>
                    <p className="mt-6 text-center text-sm text-mocha">Already have an account? <Link to="/login" className="font-medium text-caramel hover:underline">Sign in</Link></p>
                </motion.div>
            </div>
            <div className="hidden bg-caramel-dark lg:block" style={{ backgroundImage: "url(/img/cheesecake.jpg)", backgroundSize: "cover", backgroundPosition: "center" }} />
        </div>
    );
}
